import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  CircularProgress,
  createStyles,
  Divider,
  makeStyles,
  Theme,
  Typography,
} from '@material-ui/core';
import Card from '@material-ui/core/Paper';
import Grid from '@material-ui/core/Grid';
import { isEmpty } from 'lodash';
import { useApolloClient } from '@apollo/react-hooks';

import useAuth from '../../../../hooks/useAuth';
import colors from '../../../../themes/main-colors';
import OutageMapPanelHeader from './header/OutageMapPanelHeader';
import SignInBlock from './user-details/SignInBlock';
import RestorationStatus from './restoration-status/RestorationStatus';
import OutageDetailsSection from './outage-details/OutageDetailsSection';
import OutageStatus from './outage-status/OutageStatus';
import OutageLocation from './outage-location/OutageLocation';
import PGEButton from '../../../buttons';
import { useTranslation } from '../../../../hooks/useTranslation';
import useUtils from '../utils';
import OutageZipCode from './outage-zipcode/OutageZipcode';
import {
  OutageDetailsByZipCode,
  OutageDetailsByCounty,
  Maybe,
  AccountDetail,
  NotificationType,
  DeliveryType,
  AccountDetailParams,
  CommPreferencesParams,
  AccountCustomer,
  ZipCodeInfo,
  NotificationFor,
  OutageLookupMultipleOption,
} from '../../../../__generated__/pge-types';
import OutagePanelLayout from './outage-panel-layout/OutagePanelLayout';
import { useReportOutageApi } from './useOutageReportApi';
import useWrapWithLoader from '../../../../hooks/useWrapWithLoading';
import { useOutageEvents } from '../../useOutageEvents';
import { NotificationRequestAccountParams } from '../../OutageCenterPage';
import { getAccountNotificationsDetails } from '../../../../queries/outage.query';
import { Model } from '../../report-outage/serviceLocation.rules';
import { NotificationsContext } from '../../../../providers/NotificationsProvider';
import { TransitionContext } from '../../../../providers/TransitionProvider';
import { DrawerSnapType } from '../bottom-drawer-layout/BottomDrawerLayout';
import useGTMUtil from '../../../../lib/useGTMUtil';
import { featureFlagsContext } from '../../../../providers/FeatureFlagsProvider';
import { setOutageReportedStatusForMedallia } from '../../../../lib/gtmService';
import { formatEstimatedTimeOn } from '../../outageUtils';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    blueCard: {
      background: colors.modalBlue,
      boxShadow: 'none',
      padding: '12px 16px',
      margin: '0 0 12px',
    },
    outageBtn: {
      width: '100%',
      marginTop: '10px',
      marginBottom: '10px',
      '&.MuiButton-outlined:hover': {
        color: colors.white,
      },
    },
    notificationOpted: {
      paddingLeft: '10px',
      marginTop: '10px',
    },
    outageBtnSecondary: {
      background: colors.orange,
      '&:hover': {
        background: colors.orange,
      },
    },
    outageMapPanelContent: {
      width: '100%',
      overflow: 'hidden',
      overflowY: 'auto',
      [theme.breakpoints.down('sm')]: {
        height: 'auto',
      },
    },
    userDetailsBlock: {
      marginBottom: '20px',
    },
    disconnectedMeterStatusText: {
      paddingLeft: '10px',
    },
  }),
);

export type GuestOutageDetail = {
  isEligibleToReport?: boolean | null | undefined;
  isAccountExists?: boolean | null | undefined;
  encryptedAccountNumber?: string | null | undefined;
  encryptedPremiseId?: string | null | undefined;
  encryptedServicePointId?: string | null | undefined;
  hasKnownOutage?: boolean | null | undefined;
  houseNumber?: string | null | undefined;
  registeredPhone?: string | null | undefined;
  callbackPhone?: string | null | undefined;
  callbackRequested?: boolean | null | undefined;
  estimatedTimeOut?: string | null | undefined;
  estimatedTimeOn?: string | null | undefined;
  numberOfCustomersAffected?: number | null | undefined;
  numberOfReports?: number | null | undefined;
  crewDispatchedStatus?: string | null | undefined;
  isCrewOnsite?: boolean | null | undefined;
  cause?: string | null | undefined;
  ineligibleReason?: string | null | undefined;
  eventStatus?: string | null | undefined;
  showConnectionInfo?: boolean | null | undefined;
  showResetInfo?: boolean | null | undefined;
};

export type outageDataType = GuestOutageDetail & {
  accountNumber?: string | undefined;
  accountIdentifier?: string | undefined;
  encryptedServicePointIds?: Maybe<Array<string>> | undefined;
  multiplePremiseList?: Maybe<Array<OutageLookupMultipleOption>>;
};

export type OutageResultPanelData = {
  outageData: outageDataType;
  isReported: boolean;
  isNotificationSubmit: boolean;
  isOutageByZipCode: boolean;
  isOutageByEvent: boolean;
  isOutageNotFound?: boolean;
  isOutageByLookup?: boolean;
  isOutageByAccount?: boolean;
  shouldRefetchAccount?: boolean;
  selectedCountyName?: string | null | undefined;
  selectedZipCode?: string | null | undefined;
  selectedZipIndex?: number | null | undefined;
  zipCodes?: Array<ZipCodeInfo> | undefined | null;
};

interface Props {
  handleOutageByZipClick: (show: boolean) => void;
  data: OutageResultPanelData;
  onClose: () => void;
  reportOutage: () => void;
  onReportOutage: (isReportSuccess: boolean) => void;
  handleNotificationPopup: () => void;
  lastUpdateOutageDataTime?: string;
  loggedInUserNotificationRequestAccountParams: NotificationRequestAccountParams;
  setNotificationFormValues: React.Dispatch<React.SetStateAction<Model>>;
  isChangeNotificationSettingsPopup: boolean;
  notifFormSuccessfullySubmitted: boolean;
  notificationFormValues: Model;
  isNativeMobileAppPage?: boolean;
  meterStatus?: string;
  isEligibleForReconnect?: boolean;
  isOtherOrAnonymousFlow?: boolean;
}

export const OutageResultsPanel = (props: Props) => {
  const apolloClient = useApolloClient();
  const { isAuthenticated } = useAuth();
  const { t, richT } = useTranslation();
  const { isPlannedDownTime } = useContext(featureFlagsContext);
  const {
    data,
    onClose,
    onReportOutage,
    reportOutage,
    handleOutageByZipClick,
    handleNotificationPopup,
    lastUpdateOutageDataTime,
    loggedInUserNotificationRequestAccountParams,
    setNotificationFormValues,
    notificationFormValues,
    isNativeMobileAppPage,
    notifFormSuccessfullySubmitted,
    meterStatus,
    isEligibleForReconnect,
    isOtherOrAnonymousFlow,
  } = props;
  const classes = useStyles();
  const [isOutageReported, setIsOutageReported] = useState<boolean>(false);
  const [
    showNotifSuccessfullySubscribed,
    setShowNotifSuccessfullySubscribed,
  ] = useState<boolean>(false);
  const { setOutageMapNotification } = useOutageEvents();
  const notificationContext = useContext(NotificationsContext);

  const { getRequestSource, setErrorNotification } = useUtils();
  const { postGTMModalVisibilityData } = useGTMUtil();
  const customerCareNumber = t('CUSTOMER_CARE_NUMBER_OTHER');

  useEffect(() => {
    setIsOutageReported(Boolean(data?.outageData?.hasKnownOutage));
  }, [data.outageData]);

  //Push modal visibility status to the GTM
  useEffect(() => {
    const GTMPageData = {
      page: 'Outage Details',
      siteSection: 'Outage Details Panel',
    };

    postGTMModalVisibilityData({ ...GTMPageData, isVisible: true });

    return () =>
      postGTMModalVisibilityData({ ...GTMPageData, isVisible: false });
  }, []);

  const isSignedInFlow =
    isAuthenticated &&
    Boolean(loggedInUserNotificationRequestAccountParams.accountNumber) &&
    Boolean(loggedInUserNotificationRequestAccountParams.encryptedPersonId);

  const [
    isNotificationPreferencesLoading,
    setIsNotificationPreferencesLoading,
  ] = useState<boolean>(isSignedInFlow);

  const [isZipChangeLoading, setIsZipChangeLoading] = useState<boolean>(false);

  const {
    getOutageDetailsByZipCode,
    getOutagesByCounty,
    reportOutageForGuest,
    reportOutageForLoggedInUser,
    fetchContactDetails,
  } = useReportOutageApi();

  let customer: AccountCustomer;
  isAuthenticated && void fetchContactDetails().then(res => (customer = res));

  const [zipCodeData, setZipCodeData] = useState<OutageDetailsByZipCode>({
    zipCode: '',
    cause: '',
    lat: 0,
    long: 0,
    totalCustomersImpacted: 0,
  });
  const { wrapWithLoader } = useWrapWithLoader();
  const { setState: updateTransition } = useContext(TransitionContext);
  const [countyData, setCountyData] = useState<OutageDetailsByCounty>({
    totalCustomersImpacted: 0,
    outages: 0,
    county: '',
    zipCodeInfo: [],
  });

  const [zipCodeActions, setZipCodeActions] = useState({
    prevBtn: false,
    nextBtn: false,
    selectedIndex: 0,
  });

  const setErrorOnOutage = () => {
    window.scrollTo(0, 0);
    setErrorNotification(true);
    updateTransition({ open: false });
    setIsZipChangeLoading(false);
  };

  /*
  notificationsWereModifiedInPast key is used to mark an account that has modified
  their notification preferences atleast once.
  The way we are determining this is by check if any premise has a valid callbackPhoneNumber
  and if commPreferences is not null.
  We are running a loop and breaking as soon as we find the truthy info as scanning the complete
  data is not required.
  */

  let notificationsWereModifiedInPast = false;
  const defaultFormValues: Model = {
    receiveEmail: false,
    email: '',
    receiveText: false,
    textNumber: '',
    callbackPhoneNumber: '',
    receiveCallBack: false,
    encryptedEmailContactId: '',
    encryptedEmailPreferenceId: '',
    encryptedTextNumberContactId: '',
    encryptedTextNumberPreferenceId: '',
  };
  const setupForm = (accountDetails: AccountDetail | undefined) => {
    const callbackPhoneNumberInfo = accountDetails?.premiseInfo?.find(
      premise => {
        return premise?.servicePointDetails?.find(servicePoint => {
          defaultFormValues.callbackPhoneNumber =
            servicePoint?.outageInfo?.callbackPhone?.replace(/\D/g, '') || '';
          defaultFormValues.receiveCallBack = Boolean(
            servicePoint?.outageInfo?.callbackRequested,
          );
          return Boolean(servicePoint?.outageInfo?.callbackPhone);
        });
      },
    );

    notificationsWereModifiedInPast =
      !isEmpty(callbackPhoneNumberInfo) ||
      Boolean(accountDetails?.commPreferences);

    if (Boolean(accountDetails?.commPreferences)) {
      accountDetails?.commPreferences &&
        accountDetails?.commPreferences[0]?.deliveryTypeDetails?.map(
          contactItem => {
            if (contactItem?.contactDetails) {
              if (contactItem?.deliveryType === DeliveryType.Email) {
                defaultFormValues.receiveEmail = Boolean(
                  contactItem.contactDetails[0]?.isSelected,
                );
                defaultFormValues.email =
                  contactItem.contactDetails[0]?.value || '';
                defaultFormValues.encryptedEmailContactId =
                  contactItem.contactDetails[0]?.encryptedContactId || '';
                defaultFormValues.encryptedEmailPreferenceId =
                  contactItem.contactDetails[0]?.encryptedPreferenceId || '';
              } else if (contactItem?.deliveryType === DeliveryType.Sms) {
                defaultFormValues.receiveText = Boolean(
                  contactItem.contactDetails[0]?.isSelected,
                );
                defaultFormValues.textNumber =
                  contactItem.contactDetails[0]?.value?.replace(/\D/g, '') ||
                  '';
                defaultFormValues.encryptedTextNumberContactId =
                  contactItem.contactDetails[0]?.encryptedContactId || '';
                defaultFormValues.encryptedTextNumberPreferenceId =
                  contactItem.contactDetails[0]?.encryptedPreferenceId || '';
              }
            }
          },
        );
    }

    if (!Boolean(defaultFormValues.email)) {
      const emailContact = customer?.contactDetails?.find(contact => {
        return (
          (contact?.contactType === 'EMAIL' ||
            contact?.contactType === 'BEMAIL') &&
          contact?.isPrimary === true
        );
      });
      defaultFormValues.email = emailContact?.contactValue || '';
      defaultFormValues.receiveEmail = false;
      defaultFormValues.encryptedEmailContactId =
        (!Boolean(defaultFormValues.encryptedEmailContactId) &&
          emailContact?.encryptedContactId) ||
        '';
      defaultFormValues.encryptedEmailPreferenceId = null;
    }
    if (!Boolean(defaultFormValues.textNumber)) {
      const textContact = customer?.contactDetails?.find(contact => {
        return contact?.contactType === 'MOB';
      });
      defaultFormValues.textNumber =
        textContact?.contactValue?.replace(/\D/g, '') || '';
      defaultFormValues.receiveText = false;
      defaultFormValues.encryptedTextNumberContactId =
        (!Boolean(defaultFormValues.encryptedTextNumberContactId) &&
          textContact?.encryptedContactId) ||
        '';
      defaultFormValues.encryptedTextNumberPreferenceId = null;
    }

    if (!Boolean(defaultFormValues.callbackPhoneNumber)) {
      const pnpPhone = customer?.contactDetails?.find(contact => {
        return contact?.contactType === 'PNP';
      });
      if (pnpPhone) {
        defaultFormValues.callbackPhoneNumber =
          pnpPhone?.contactValue?.replace(/\D/g, '') || '';
      } else {
        const mobPhone = customer?.contactDetails?.find(contact => {
          return contact?.contactType === 'MOB';
        });
        defaultFormValues.callbackPhoneNumber =
          mobPhone?.contactValue?.replace(/\D/g, '') || '';
      }
    }

    setNotificationFormValues(defaultFormValues);
    if (
      defaultFormValues.receiveCallBack ||
      defaultFormValues.receiveEmail ||
      defaultFormValues.receiveText
    ) {
      setShowNotifSuccessfullySubscribed(true);
    }
  };

  const accountParams = [loggedInUserNotificationRequestAccountParams];
  const getNotificationPreferences = async () => {
    if (isSignedInFlow) {
      try {
        setIsNotificationPreferencesLoading(true);
        const result = await apolloClient.query<
          { getAccountDetails: Array<AccountDetail> },
          {
            params: AccountDetailParams;
            commPreferencesParams: CommPreferencesParams;
          }
        >({
          query: getAccountNotificationsDetails,
          variables: {
            params: {
              accountNumberList: accountParams,
            },
            commPreferencesParams: {
              notificationFor: NotificationFor.Outage,
            },
          },
          fetchPolicy: 'network-only',
        });
        setupForm(result?.data?.getAccountDetails[0]);
        setIsNotificationPreferencesLoading(result.loading);
      } catch {
        setErrorNotification(true);
      }
    }
  };

  useEffect(() => {
    void getNotificationPreferences();
  }, [notifFormSuccessfullySubmitted]);

  const handleOutageAction = wrapWithLoader(
    async (isFromReportOutage = false) => {
      if (
        !data.outageData.isAccountExists ||
        !data.outageData.isEligibleToReport
      ) {
        onReportOutage(false);
        setOutageReportedStatusForMedallia(0);
      } else {
        if (!data.outageData.hasKnownOutage && !isOutageReported) {
          try {
            let outageResponse;
            if (
              isAuthenticated &&
              Boolean(
                loggedInUserNotificationRequestAccountParams.encryptedPersonId,
              ) &&
              Boolean(
                loggedInUserNotificationRequestAccountParams.accountNumber,
              )
            ) {
              outageResponse =
                data.outageData.encryptedPremiseId &&
                data.outageData.encryptedServicePointId &&
                (await reportOutageForLoggedInUser({
                  accountNumber:
                    loggedInUserNotificationRequestAccountParams.accountNumber,
                  encryptedPersonId:
                    loggedInUserNotificationRequestAccountParams.encryptedPersonId,
                  encryptedPremiseId: data.outageData.encryptedPremiseId,
                  encryptedServicePointList: data?.outageData
                    ?.encryptedServicePointIds
                    ? data.outageData.encryptedServicePointIds
                    : [data.outageData.encryptedServicePointId],
                  source: getRequestSource(),
                }));
            } else {
              outageResponse =
                data.outageData.encryptedServicePointId &&
                data.outageData.encryptedAccountNumber &&
                (await reportOutageForGuest({
                  encryptedAccountNumber:
                    data.outageData.encryptedAccountNumber,
                  encryptedServicePointList: data?.outageData
                    ?.encryptedServicePointIds
                    ? data.outageData.encryptedServicePointIds
                    : [data.outageData.encryptedServicePointId],
                  source: getRequestSource(),
                }));
            }
            if (!outageResponse) {
              setErrorOnOutage();
            } else {
              setIsOutageReported(true);
              onReportOutage(true);
              if (isFromReportOutage) {
                setOutageMapNotification('success', 'REPORT_SUCCESSFUL');
                setOutageReportedStatusForMedallia(1);
              }
            }
          } catch {
            setErrorOnOutage();
          }
        }
        if (isOutageReported || data.outageData.hasKnownOutage) {
          handleNotificationPopup();
        }
      }
    },
  );

  const zipCodeDataSet = (zipCodeOutageInfo: OutageDetailsByZipCode) => {
    setZipCodeData(zipCodeOutageInfo);
    setIsZipChangeLoading(false);
  };

  const handleOutageZipChange = async (isPrev: boolean) => {
    let currZipIndex = zipCodeActions.selectedIndex;
    if (currZipIndex >= 0) {
      currZipIndex = isPrev ? currZipIndex - 1 : currZipIndex + 1;
      const tempZipInfoLength = data?.zipCodes?.length
        ? data?.zipCodes?.length
        : 0;

      if (currZipIndex < 0) {
        currZipIndex = 0;
      } else if (
        tempZipInfoLength !== 0 &&
        currZipIndex === tempZipInfoLength
      ) {
        currZipIndex = tempZipInfoLength - 1;
      }
      setZipCodeActions({
        prevBtn: currZipIndex > 0 ? true : false,
        nextBtn: currZipIndex < tempZipInfoLength - 1 ? true : false,
        selectedIndex: currZipIndex,
      });
      currZipIndex =
        currZipIndex < 0
          ? 0
          : currZipIndex >= tempZipInfoLength
          ? tempZipInfoLength - 1
          : currZipIndex;
      const finalZipCode =
        data &&
        data?.zipCodes &&
        data?.zipCodes[currZipIndex] &&
        data?.zipCodes[currZipIndex]?.zipCode
          ? data?.zipCodes[currZipIndex]?.zipCode
          : zipCodeData?.zipCode;
      if (finalZipCode) {
        setIsZipChangeLoading(true);
        try {
          await outageDetailsByZipCode(finalZipCode);
        } catch {
          setErrorOnOutage();
        }
      } else {
        setErrorOnOutage();
      }
    }
  };

  const outageDetailsByZipCode = async (tempZipCode: string) => {
    setIsZipChangeLoading(true);
    try {
      const zipCodeDetsResponse = await getOutageDetailsByZipCode({
        zipCode: tempZipCode,
      });
      zipCodeDetsResponse && zipCodeDetsResponse?.getOutageDetailsByZipCode
        ? zipCodeDataSet(zipCodeDetsResponse?.getOutageDetailsByZipCode)
        : setErrorOnOutage();
      setIsZipChangeLoading(false);
    } catch {
      setErrorOnOutage();
    }
  };

  const getOutageZipCodeData = wrapWithLoader(async () => {
    try {
      const tempZipcode = data.selectedZipCode
        ? data.selectedZipCode
        : data &&
          data?.zipCodes &&
          data?.zipCodes[0] &&
          data?.zipCodes[0]?.zipCode;
      if (tempZipcode) {
        const currZipIndex = data.selectedZipIndex ? data.selectedZipIndex : 0;
        if (currZipIndex >= 0) {
          const tempZipInfoLength = data?.zipCodes?.length
            ? data?.zipCodes?.length
            : 0;
          setZipCodeActions({
            prevBtn: currZipIndex > 0 ? true : false,
            nextBtn: currZipIndex < tempZipInfoLength - 1 ? true : false,
            selectedIndex: currZipIndex,
          });
        }
        tempZipcode && (await outageDetailsByZipCode(tempZipcode));
      } else {
        setErrorOnOutage();
      }
    } catch {
      setErrorOnOutage();
    }
  });

  useEffect(() => {
    if (data.isOutageByZipCode) {
      void getOutageZipCodeData();
    }
  }, [data.selectedCountyName, data.selectedZipCode]);

  useEffect(() => {
    setShowNotifSuccessfullySubscribed(
      Boolean(
        isSignedInFlow &&
          (notificationFormValues.receiveCallBack ||
            notificationFormValues.receiveEmail ||
            notificationFormValues.receiveText),
      ),
    );
  }, [loggedInUserNotificationRequestAccountParams, notificationFormValues]);

  return (
    <OutagePanelLayout refId={2} defaultSnap={DrawerSnapType.default}>
      <Grid item>
        <OutageMapPanelHeader
          showHomeIcon={
            Boolean(!data.isOutageByEvent) && Boolean(!data.isOutageByZipCode)
          }
          hasKnownOutage={isOutageReported || data.isReported}
          isAuthenticated={isAuthenticated}
          outageCount={
            Boolean(data.isOutageByEvent)
              ? Number(data?.outageData?.numberOfCustomersAffected)
              : Boolean(data.isOutageByZipCode)
              ? Number(zipCodeData?.totalCustomersImpacted)
              : undefined
          }
          showOutageCount={!data.isOutageNotFound}
          title={
            !data.outageData.isAccountExists && !data.isOutageByZipCode
              ? data.isOutageNotFound
                ? t('OUTAGE_NOT_FOUND')
                : t('ADDRESS_NOT_FOUND')
              : !data.outageData.isEligibleToReport && !data.isOutageByZipCode
              ? t('ACCOUNT_NOT_FOUND_TITLE')
              : !data.outageData.hasKnownOutage &&
                !data.isOutageByZipCode &&
                !isOutageReported &&
                !data.isReported
              ? t('NO_OUTAGE_HEADER')
              : isAuthenticated &&
                !data.isOutageByZipCode &&
                !data.isOutageByLookup &&
                !data.isOutageByEvent
              ? t('USER_OUTAGE_HEADER')
              : t('OUTAGE_REPORTED')
          }
          closeBtnVisible={true}
          onHandleClose={onClose}
        />
      </Grid>
      {((!data.isOutageByZipCode && !data.isOutageByEvent) ||
        data.isOutageNotFound) && (
        <Grid item>
          <OutageLocation
            alternateLoc={data.isOutageByLookup || data.isOutageNotFound}
            houseNumber={data.outageData?.houseNumber}
            accountIdentifier={data.outageData?.accountIdentifier}
            hideLocationHeader={
              !data.outageData.isAccountExists ||
              !data.outageData.isEligibleToReport
            }
            locationText={
              !data.outageData.isAccountExists
                ? data.isOutageNotFound
                  ? t('SELECTED_LOCATION_NO_LONGER_HAS_OUTAGE_REPORTED')
                  : richT('OUTAGE_LOCATION_NOT_FOUND', {
                      CALL_FOR_ASSISTANCE_NUMBER: t(
                        'CALL_FOR_ASSISTANCE_NUMBER',
                      ),
                    })
                : !data.outageData.isEligibleToReport
                ? richT('OUTAGE_ACCOUNT_NOT_SA', {
                    CALL_FOR_ASSISTANCE_NUMBER: t('CALL_FOR_ASSISTANCE_NUMBER'),
                  })
                : null
            }
          />
        </Grid>
      )}
      {isAuthenticated &&
        !data.outageData.hasKnownOutage &&
        (meterStatus === 'on' || meterStatus === 'powerQuality') && (
          <div>
            <Typography
              className={classes.disconnectedMeterStatusText}
              variant={'subtitle2'}
              style={{ color: 'green' }}
            >
              {t('METER_STATUS_ON_TEXT')}
            </Typography>
          </div>
        )}
      {!isAuthenticated &&
        isOtherOrAnonymousFlow &&
        !data.outageData.hasKnownOutage &&
        data.outageData?.showResetInfo && (
          <div>
            <Typography
              className={classes.disconnectedMeterStatusText}
              variant={'subtitle2'}
              style={{ color: 'green' }}
            >
              {t('METER_STATUS_ON_TEXT')}
            </Typography>
          </div>
        )}
      {isOtherOrAnonymousFlow && data.outageData?.showConnectionInfo && (
        <div>
          <Typography
            className={classes.disconnectedMeterStatusText}
            variant={'subtitle2'}
            style={{ color: 'red' }}
          >
            {t('METER_STATUS_DISCONNECTED_ELIGIBITITY_FALSE_UNAUTHERIZED')}
            {
              <a
                href={'tel:' + customerCareNumber}
                style={{
                  textDecoration: 'underline',
                  color: colors.brightBlue,
                }}
              >
                {' '}
                {customerCareNumber}{' '}
              </a>
            }
            {t('TO_GET_RECONNECTED_UNAUTHERIZED')}
          </Typography>
        </div>
      )}
      {isAuthenticated &&
        meterStatus === 'disconnected' &&
        !isEligibleForReconnect && (
          <div>
            <Typography
              className={classes.disconnectedMeterStatusText}
              variant={'subtitle2'}
              style={{ color: 'red' }}
            >
              {t('METER_STATUS_DISCONNECTED_ELIGIBITITY_FALSE')}
              {
                <a
                  href={'tel:' + customerCareNumber}
                  style={{
                    textDecoration: 'underline',
                    color: colors.brightBlue,
                  }}
                >
                  {' '}
                  {customerCareNumber}{' '}
                </a>
              }
              {t('TO_GET_RECONNECTED')}
            </Typography>
          </div>
        )}
      {isAuthenticated &&
        meterStatus === 'disconnected' &&
        isEligibleForReconnect && (
          <div>
            <Typography
              className={classes.disconnectedMeterStatusText}
              variant={'subtitle2'}
              style={{ color: 'red' }}
            >
              {t('METER_STATUS_DISCONNECTED_ELIGIBITITY_TRUE')}
            </Typography>
          </div>
        )}
      <Divider style={{ marginBottom: '1rem', marginTop: '1rem' }} />
      {data.isOutageByZipCode && (
        <Grid item>
          <OutageZipCode
            isPrevEnabled={zipCodeActions.prevBtn}
            isNextEnabled={zipCodeActions.nextBtn}
            selectedZipcode={zipCodeData?.zipCode}
            handleOutageByZipClick={handleOutageByZipClick}
            handleOutageZipChange={handleOutageZipChange}
            isLoading={isZipChangeLoading}
          />
        </Grid>
      )}
      <div className={classes.outageMapPanelContent}>
        {((!data.outageData.hasKnownOutage &&
          !data.isOutageByZipCode &&
          data.outageData.isAccountExists &&
          data.outageData.isEligibleToReport) ||
          data.isOutageNotFound) &&
          !(isAuthenticated && meterStatus === 'disconnected') &&
          !(isOtherOrAnonymousFlow && data.outageData?.showConnectionInfo) && (
            <Grid item>
              <OutageStatus
                isOutageReported={isOutageReported || data.isReported}
                statusMessage={
                  data.isOutageNotFound
                    ? richT('OUTAGE_NOT_FOUND_MESSAGE')
                    : richT('NO_OUTAGE_REPORTED_MESSAGE')
                }
              />
            </Grid>
          )}
        <Grid
          item
          className={classes.userDetailsBlock}
          data-testid="UserDetailsBlock"
        >
          {!isPlannedDownTime && !isAuthenticated && !isNativeMobileAppPage && (
            <Card className={classes.blueCard}>
              <SignInBlock />
            </Card>
          )}
        </Grid>
        {(data.outageData.hasKnownOutage || data.isOutageByZipCode) && (
          <Grid item>
            <OutageDetailsSection
              outageDetail={{
                estimatedTimeOn: data.isOutageByZipCode
                  ? zipCodeData.estimatedRestorationOn
                  : formatEstimatedTimeOn(data.outageData?.estimatedTimeOn),
                estimatedTimeOut: data.isOutageByZipCode
                  ? zipCodeData.estimatedRestorationOut
                  : data.outageData.estimatedTimeOut,
                cause: data.isOutageByZipCode
                  ? zipCodeData.cause
                  : data.outageData.cause,
                numberOfCustomersAffected: data.isOutageByZipCode
                  ? zipCodeData.totalCustomersImpacted
                  : data.outageData.numberOfCustomersAffected,
              }}
              lastUpdateOutageDataTime={lastUpdateOutageDataTime}
              isCrewOnsite={data?.outageData?.isCrewOnsite}
            />
          </Grid>
        )}

        {!data.outageData.hasKnownOutage &&
          !isOutageReported &&
          !data.isReported &&
          !data.isOutageByZipCode && (
            <Grid item>
              <PGEButton
                className={`${classes.outageBtn} ${classes.outageBtnSecondary}`}
                onClick={() => reportOutage()}
                disabled={
                  (isAuthenticated &&
                    meterStatus === 'disconnected' &&
                    !isEligibleForReconnect) ||
                  (isOtherOrAnonymousFlow &&
                    data.outageData?.showConnectionInfo) ||
                  false
                }
              >
                {isAuthenticated &&
                meterStatus === 'disconnected' &&
                isEligibleForReconnect
                  ? t('GET_RECONNECTED')
                  : t('REPORT_OUTAGE')}
              </PGEButton>
            </Grid>
          )}
        {/* // winter storm 2024: temporarily hide phone field - added isAuthenticated below */}
        {isAuthenticated &&
        (data.outageData.hasKnownOutage ||
          isOutageReported ||
          data.isReported) &&
        !data.isOutageByEvent &&
        !notificationContext?.state?.isOpen &&
        !data?.isOutageByZipCode ? (
          isNotificationPreferencesLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center">
              <CircularProgress />
            </Box>
          ) : (
            <>
              {showNotifSuccessfullySubscribed ? (
                <Typography
                  className={classes.notificationOpted}
                  variant={'subtitle1'}
                >
                  {t('NOTIF_OPTED_SUCCESS')}
                </Typography>
              ) : null}
              {!data.isOutageByLookup ? (
                <Grid item>
                  <PGEButton
                    className={classes.outageBtn}
                    onClick={() => handleOutageAction(false)}
                    color={'primary'}
                    style={{ marginTop: '5px', marginBottom: '15px' }}
                  >
                    {showNotifSuccessfullySubscribed
                      ? t('CHANGE_NOTIFICATION_SETTINGS')
                      : t('GET_STATUS_ALERTS')}
                  </PGEButton>
                </Grid>
              ) : (
                ''
              )}
            </>
          )
        ) : null}
        {(data.outageData.hasKnownOutage || data.isOutageByZipCode) && (
          <Grid item>
            <Card className={classes.blueCard}>
              <RestorationStatus
                isCrewOnsite={
                  data.isOutageByZipCode ? null : data.outageData.isCrewOnsite
                }
                crewDispatchedStatus={
                  data.isOutageByZipCode
                    ? null
                    : data.outageData.crewDispatchedStatus
                }
                cause={
                  data.isOutageByZipCode
                    ? zipCodeData.cause
                    : data.outageData.cause
                }
                eventStatus={
                  data.isOutageByZipCode ? null : data.outageData?.eventStatus
                }
              />
            </Card>
            <Typography css={{ fontSize: '12px' }}>
              <Typography component="span" css={{ color: 'red' }}>
                *
              </Typography>{' '}
              {t('REPORT_OUTAGE_FOOTNOTE')}
            </Typography>
          </Grid>
        )}
      </div>
    </OutagePanelLayout>
  );
};

export default OutageResultsPanel;
